import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  useFlags,
  usePass,
  usePassClaim,
  usePassClaimAll,
  usePassLevelToastAck,
} from '../api/hooks';
import { ErrorView, Loader } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { RewardPreview } from '../components/pass/RewardPreview';
import { gamesToLevel, lastShownLevel, markShownLevel, seasonTimer, toNextLabel } from '../components/pass/level';
import { plural } from '../format';
import { RARITY_COLOR, RARITY_LABEL, rarityTextStyle, rarityTint } from '../components/cosmetics/cosmeticsMap';
import { backButton, haptics } from '../telegram';
import { track } from '../analytics';
import { SheetClose } from '../components/SheetClose';
import type { PassCell, PassQuest, PassQuests } from '../api/types';

const ROW_H = 72;

/** 6.1 — дорожка Straddle Pass: 30 уровней, «Бесплатно» слева, Pass+ справа,
 *  между ними линия с узлами; автоскролл к текущему уровню. */
export function PassScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const pass = usePass(enabled);
  const claim = usePassClaim();
  const claimAll = usePassClaimAll();
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrolled = useRef(false);
  // Тост нового уровня: ловим при входе, дальше живём локальным состоянием
  // (ack сразу — при следующем входе тост не повторится).
  const toastAck = usePassLevelToastAck();
  const [justClaimed, setJustClaimed] = useState<string | null>(null);
  const [toastLevel, setToastLevel] = useState<number | null>(null);
  const toastCaught = useRef(false);
  const toastTouchY = useRef<number | null>(null);
  useEffect(() => {
    const lvl = pass.data?.me?.levelToast ?? null;
    if (lvl === null || toastCaught.current) return;
    toastCaught.current = true;
    setToastLevel(lvl);
    haptics.light();
    toastAck.mutate();
    window.setTimeout(() => setToastLevel(null), 6000);
  }, [pass.data?.me?.levelToast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  const level = pass.data?.me?.level ?? 1;
  useEffect(() => {
    if (scrolled.current || !pass.data?.tracks || !listRef.current) return;
    scrolled.current = true;
    listRef.current.scrollTop = Math.max(0, (level - 2.5) * ROW_H);
  }, [pass.data, level]);

  // Анимация прироста уровня (handoff 4 §2.1): полоса героя от последнего
  // показанного значения со сбросом на целых, flip числа, подсветка новых
  // рядов, автоскролл от старого ряда к новому. Показывается один раз (§2.4).
  const [lvlAnim, setLvlAnim] = useState<{ fromLevel: number } | null>(null);
  const [heroFrac, setHeroFrac] = useState<number | null>(null); // -1 = сброс без transition
  const [heroDur, setHeroDur] = useState(500); // длительность текущего участка полосы
  const [heroLevel, setHeroLevel] = useState<number | null>(null);
  const [pulseOn, setPulseOn] = useState(false); // кольцо узла + подсветка ряда (момент пересечения)
  const [revealOn, setRevealOn] = useState(false); // 800 мс: маркер, ячейки, автоскролл
  const [toastHold, setToastHold] = useState(false); // тост — на 1200 мс (§2.1)
  const animStarted = useRef(false);
  useEffect(() => {
    const d = pass.data;
    if (animStarted.current || !d?.me || !d.season || d.status !== 'active') return;
    animStarted.current = true;
    const cur = d.me;
    const prev = lastShownLevel();
    markShownLevel(cur.level, cur.xp);
    const grew = prev !== null && cur.xp > prev.xp && cur.level >= prev.level;
    if (!prev || !grew) return;
    if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const crossings = cur.level - prev.level;
    const fromFrac = (prev.xp % 100) / 100;
    const toFrac = (cur.xp % 100) / 100;
    setLvlAnim({ fromLevel: prev.level });
    setHeroLevel(prev.level);
    setHeroFrac(fromFrac);
    setToastHold(true);
    requestAnimationFrame(() => {
      if (listRef.current && crossings > 0)
        listRef.current.scrollTop = Math.max(0, (prev.level - 2.5) * ROW_H);
    });
    const ts: number[] = [];
    if (crossings === 0) {
      // прирост без пересечения целого — только полоса (500 мс)
      ts.push(
        window.setTimeout(() => {
          setHeroDur(500);
          setHeroFrac(toFrac);
        }, 300),
      );
    } else if (crossings >= 3) {
      // §2.4: пересечения не тянутся — один flip и один проход полосы
      ts.push(
        window.setTimeout(() => {
          setHeroDur(500);
          setHeroFrac(toFrac);
        }, 300),
      );
      ts.push(
        window.setTimeout(() => {
          setHeroLevel(cur.level);
          haptics.light();
        }, 550),
      );
    } else {
      // 500 мс на ВЕСЬ путь: до 100% → мгновенный сброс → до новой дроби
      const path = 1 - fromFrac + (crossings - 1) + toFrac;
      const d1 = Math.max(80, Math.round((500 * (1 - fromFrac)) / Math.max(0.2, path)));
      const d2 = Math.max(80, 500 - d1);
      ts.push(
        window.setTimeout(() => {
          setHeroDur(d1);
          setHeroFrac(1);
        }, 300),
      );
      ts.push(
        window.setTimeout(() => {
          setHeroFrac(-1); // сброс в 0 без transition
          setHeroLevel(cur.level); // flip числа — в момент пересечения
          haptics.light();
          setPulseOn(true); // кольцо узла ×2 + подсветка ряда
        }, 300 + d1),
      );
      ts.push(
        window.setTimeout(() => {
          setHeroDur(d2);
          setHeroFrac(toFrac);
        }, 300 + d1 + 40),
      );
    }
    ts.push(
      window.setTimeout(() => {
        setRevealOn(true); // маркер переезжает 400 мс, ячейки closed→avail
        if (listRef.current && crossings > 0)
          listRef.current.scrollTo({ top: Math.max(0, (cur.level - 2.5) * ROW_H), behavior: 'smooth' });
      }, 800),
    );
    ts.push(window.setTimeout(() => setToastHold(false), 1200));
    ts.push(
      window.setTimeout(() => {
        setLvlAnim(null);
        setHeroFrac(null);
        setHeroLevel(null);
        setPulseOn(false);
      }, 2600),
    );
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, [pass.data]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data) return <Loader />;
  if (pass.isLoading) return <Loader />;
  if (pass.isError || !pass.data) return <ErrorView message={(pass.error as Error)?.message} />;
  if (pass.data.status !== 'active' || !pass.data.season || !pass.data.me) {
    return (
      <div className="bp-screen">
        <Head onBack={() => navigate(-1)} />
        <div className="q-empty">Сезон Straddle Pass сейчас не идёт.</div>
      </div>
    );
  }
  const { season, me, tracks } = pass.data;

  // «Следующая награда через N игр»: ближайший уровень с наградой выше текущего.
  const nextRewardLevel =
    (tracks ?? []).find(
      (r) => r.level > me.level && ((me.tier === 'plus' && r.plus) || r.free),
    )?.level ?? null;
  const nextRewardIn =
    nextRewardLevel === null ? null : gamesToLevel(nextRewardLevel, me.xp, season.xpPerGame);

  const toastAction = () => {
    haptics.tap();
    if (listRef.current) {
      listRef.current.scrollTo({ top: Math.max(0, (me.level - 2.5) * ROW_H), behavior: 'smooth' });
    }
    setToastLevel(null);
    if (me.claimable > 0) {
      track('pass_claim_all', { count: me.claimable, from: 'level_toast' });
      void claimAll
        .mutateAsync()
        .then(() => haptics.success())
        .catch(() => haptics.error());
    }
  };

  const onCell = (cell: PassCell, el?: HTMLElement | null) => {
    if (cell.state === 'claimable') {
      haptics.tap();
      track('pass_claim', { track: cell.track, level: cell.level });
      setJustClaimed(`${cell.track}:${cell.level}`);
      void claim
        .mutateAsync({ track: cell.track, level: cell.level })
        .then(() => {
          haptics.success();
          // Награда-колода вылетает в пилюлю «Колоды» (Handoff 3, 5.2).
          if (cell.kind === 'deck' && el) flyDeckToPill(el, cell.deckRarity ?? 0);
        })
        .catch(() => haptics.error());
    } else if (cell.state === 'locked') {
      navigate('/pass/plus');
    }
  };

  return (
    <div className="bp-screen">
      <Head onBack={() => navigate(-1)} decks={me.decksUnopened} />

      {/* Шапка 6.1a по BP Pass.dc.html: уровень слева, прогресс и таймер справа */}
      <div className="bp-hero" data-theme="dark">
        <div className="bp-hero-row">
          <div className="bp-hero-left">
            <span className="bp-hero-label">
              {me.tier === 'plus' ? 'STRADDLE PASS+' : 'STRADDLE PASS'}
            </span>
            <div className="bp-hero-level mono">
              <span className="num" key={heroLevel ?? me.level}>
                {heroLevel ?? me.level}
              </span>
              <span className="u">ур.</span>
            </div>
          </div>
          <div className="bp-hero-right">
            <div className="bp-hero-line1">
              <span>
                {me.level < season.levels
                  ? `${toNextLabel(me.level + 1, me.xp, season.xpPerGame)} до ${me.level + 1}`
                  : 'максимальный уровень'}
              </span>
              <span className="tm mono">{seasonTimer(season.endsAt)}</span>
            </div>
            <div className="bp-hero-bar">
              <i
                style={{
                  width: `${Math.round(Math.max(0, heroFrac === null ? me.xpFrac : heroFrac) * 100)}%`,
                  transition: heroFrac === -1 ? 'none' : `width ${heroDur}ms ease-in-out`,
                }}
              />
            </div>
            <div className="bp-hero-line2">
              {me.guaranteeIn > 0 ? (
                <span className="bp-hero-guarantee" title={`Эпическая колода гарантирована через ${me.guaranteeIn}`}>
                  {[0, 1, 2].map((i) => (
                    <i key={i} className={i < 3 - Math.min(3, me.guaranteeIn) ? 'on' : ''} />
                  ))}
                  эпический — через {me.guaranteeIn}{' '}
                  {me.guaranteeIn === 1 ? 'игру' : me.guaranteeIn < 5 ? 'игры' : 'игр'}
                </span>
              ) : (
                <span className="bp-hero-guarantee">эпическая колода гарантирована</span>
              )}
            </div>
          </div>
        </div>
        {season.daysLeft < 7 && me.decksUnopened > 0 && (
          <div className="bp-hero-endnote">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <circle cx="12" cy="12" r="8.5" />
              <path d="M12 7.5V12l3 2" />
            </svg>
            Неоткрытые колоды откроются сами в конце сезона
          </div>
        )}
      </div>

      <div className="bp-cols-head">
        <span>Бесплатно</span>
        <span />
        <span>
          Pass+
          {me.tier !== 'plus' && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden style={{ marginLeft: 4, verticalAlign: -1 }}>
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          )}
        </span>
      </div>

      <div className="bp-track" ref={listRef}>
        {(tracks ?? []).map((row) => {
          const isNew = lvlAnim !== null && row.level > lvlAnim.fromLevel && row.level <= me.level;
          return (
            <div
              className={`bp-row${isNew && pulseOn ? ' bp-row--new' : ''}${isNew && revealOn ? ' bp-row--reveal' : ''}`}
              key={row.level}
              style={{ height: ROW_H }}
              data-level={row.level}
            >
              {row.level === me.level + 1 && (
                <div
                  className="bp-youhere"
                  aria-hidden
                  style={
                    lvlAnim
                      ? {
                          // переезд маркера со старой границы (400 мс, §2.1)
                          transform: revealOn
                            ? 'translateY(0)'
                            : `translateY(${(lvlAnim.fromLevel - me.level) * ROW_H}px)`,
                          transition: revealOn ? 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none',
                        }
                      : undefined
                  }
                >
                  <span className="pill mono">
                    ты здесь · {toNextLabel(me.level + 1, me.xp, season.xpPerGame)} до {me.level + 1}
                  </span>
                </div>
              )}
              <TrackCell cell={row.free} onTap={onCell} gamesTo={gamesToLevel(row.level, me.xp, season.xpPerGame)} justClaimed={justClaimed === `free:${row.level}`} />
              <div className={`bp-node-wrap${row.level <= me.level ? ' bp-node-wrap--past' : ''}`}>
                <span
                  className={`bp-node mono${row.level === me.level ? ' bp-node--cur' : ''}${row.level < me.level ? ' bp-node--past' : ''}${toastLevel === row.level ? ' bp-node--flash' : ''}${isNew && pulseOn ? ' bp-node--lvlup' : ''}${row.level === me.level && me.tier === 'plus' ? ' bp-node--pulse' : ''}`}
                >
                  {row.level}
                </span>
              </div>
              <TrackCell cell={row.plus} onTap={onCell} gamesTo={gamesToLevel(row.level, me.xp, season.xpPerGame)} justClaimed={justClaimed === `plus:${row.level}`} />
            </div>
          );
        })}
      </div>

      <div className="bp-bottom">
        {pass.data.quests && <QuestsRow quests={pass.data.quests} pop={revealOn} />}
        {me.tier === 'plus' ? (
          // с купленным Pass+ кнопки покупки нет; «Забрать» — только когда есть что
          me.claimable > 0 && (
          <button
            className="btn bp-claim-all"
            disabled={claimAll.isPending}
            onClick={() => {
              haptics.tap();
              track('pass_claim_all', { count: me.claimable });
              void claimAll
                .mutateAsync()
                .then(() => haptics.success())
                .catch(() => haptics.error());
            }}
          >
            Забрать {me.claimable} {plural(me.claimable, ['награду', 'награды', 'наград'])}
          </button>
          )
        ) : (
          <button className="btn bp-claim-all" onClick={() => navigate('/pass/plus')}>
            Получить Straddle Pass+ — {season.pricePlus.toLocaleString('ru-RU')} ₽
          </button>
        )}
        <div className="bp-bottom-note mono">
          {me.tier !== 'plus'
            ? 'Награда на каждом уровне · ваучеры · колоды'
            : nextRewardIn !== null
              ? `Следующая награда через ${nextRewardIn} ${nextRewardIn === 1 ? 'игру' : nextRewardIn < 5 ? 'игры' : 'игр'}`
              : 'Неоткрытые колоды откроются сами в конце сезона'}
        </div>
      </div>


      {toastLevel !== null && !toastHold && (
        <div
          className="bp-lvl-toast"
          onClick={toastAction}
          role="button"
          onTouchStart={(e) => {
            toastTouchY.current = e.touches[0]?.clientY ?? null;
          }}
          onTouchMove={(e) => {
            const y0 = toastTouchY.current;
            const y = e.touches[0]?.clientY;
            if (y0 !== null && y !== undefined && y0 - y > 24) {
              toastTouchY.current = null;
              setToastLevel(null); // свайп вверх — скрыть (спека Handoff 3)
            }
          }}
        >
          <span className="sq mono">{toastLevel}</span>
          <span className="tx">
            {/* §2.4: при приросте сразу на несколько уровней — «Ждут K наград» */}
            {me.claimable > 1 ? `Уровень ${toastLevel}! Ждут ${me.claimable} наград` : `Уровень ${toastLevel}! Награда ждёт`}
            <span className="sub2">
              +{Math.round(season.xpPerGame)} XP за игру{' '}
              {me.lastGameAt ? WEEKDAY_ACC[new Date(me.lastGameAt).getDay()] : ''}
            </span>
          </span>
          {me.claimable > 0 && <span className="act">Забрать</span>}
        </div>
      )}

    </div>
  );
}

/** Строка «Задания» над кнопкой — у всех игроков («Задания и опыт»):
 *  открывает шторку; точки — выполненные задания. */
function QuestsRow({ quests, pop = false }: { quests: PassQuests; pop?: boolean }) {
  // Возврат с экрана задания (6.1d) приходит с state.quests — шторка сразу открыта
  const location = useLocation();
  const [open, setOpen] = useState(
    Boolean((location.state as { quests?: boolean } | null)?.quests),
  );
  const all = [...quests.game, ...quests.week];
  const hasDone = quests.completed > 0;
  return (
    <>
      <button
        className={`bq-row${hasDone ? ' bq-row--done' : ''}${pop && hasDone ? ' bq-row--pop' : ''}`}
        onClick={() => {
          haptics.tap();
          track('pass_quests_open');
          setOpen(true);
        }}
      >
        <span className="bq-ic" aria-hidden>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M2.5 4l1.2 1.2L6 2.9M2.5 9l1.2 1.2L6 7.9" />
            <path d="M8.5 4.6h4M8.5 9.6h4" />
          </svg>
        </span>
        <span className="bq-main">
          <span className="nm">Задания</span>
          <span className="sub">
            {`Выполнено ${quests.completed} из ${quests.total || 4} · XP за задания`}
          </span>
        </span>
        <span className="bq-right">
          {all.map((q) => (
            <i key={q.id} className={q.done ? 'on' : ''} />
          ))}
          <span className="bq-chev">›</span>
        </span>
      </button>
      {open && <QuestsSheet quests={quests} onClose={() => setOpen(false)} />}
    </>
  );
}

function questsTimer(resetAt: string): string {
  const ms = Math.max(0, new Date(resetAt).getTime() - Date.now());
  const d = Math.floor(ms / 86400_000);
  const h = Math.floor((ms % 86400_000) / 3600_000);
  return `до пн · ${d}д ${h}ч`;
}

const DIFF_LABEL: Record<string, string> = {
  easy: 'Лёгкое',
  medium: 'Среднее',
  hard: 'Сложное',
};

/** Шторка «Задания» — используется и с экрана пасса, и с карточки игры.
 *  Портал в body: внутри .bp-bottom (sticky, z-index 3) свой stacking
 *  context, и шторка оказывалась ПОД таббаром (z-50). */
export function QuestsSheet({ quests, onClose }: { quests: PassQuests; onClose: () => void }) {
  return createPortal(
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <h3>Задания</h3>
        <div className="sheet-sub">Опыт за игры и задания · XP идёт в уровень пасса</div>
        <div className="bq-sec">
          <span>НА КАЖДУЮ ИГРУ</span>
          <span>выдаётся при записи</span>
        </div>
        {quests.game.length === 0 && (
          <div className="bq-empty">Запишись на игру — получишь задание</div>
        )}
        {quests.game.map((q) => (
          <QuestCard key={q.id} q={q} />
        ))}
        <div className="bq-sec">
          <span>НА НЕДЕЛЮ</span>
          <span>{questsTimer(quests.weekResetAt)}</span>
        </div>
        {quests.week.map((q) => (
          <QuestCard key={q.id} q={q} />
        ))}
        <div className="bq-note">
          Выполненные засчитываются сразу. Заменить задание нельзя; не пришёл — задание сгорает.
        </div>
      </div>
    </div>,
    document.body,
  );
}

function QuestCard({ q }: { q: PassQuest }) {
  const navigate = useNavigate();
  const pct = Math.min(100, Math.round((q.value / q.target) * 100));
  const isGame = q.gameId != null;
  const gameDay = q.gameStartAt
    ? new Date(q.gameStartAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
    : null;
  return (
    // handoff 5: строка задания тапабельна → экран 6.1d
    <button
      className={`bq-card${q.done ? ' bq-card--done' : ''}`}
      onClick={() => {
        haptics.tap();
        navigate(`/pass/quests/${q.id}`);
      }}
    >
      <span className="bq-sq mono">{q.done ? '✓' : `${q.value}/${q.target}`}</span>
      <span className="bq-body">
        <span className="nm">
          {q.title}
          {isGame && q.difficulty && (
            <span className={`bq-diff bq-diff--${q.difficulty}`}>{DIFF_LABEL[q.difficulty]}</span>
          )}
        </span>
        <span className="bq-prog">
          <span className="bq-bar">
            <i style={{ width: `${q.done ? 100 : pct}%` }} />
          </span>
          <span className="sub mono">
            {q.done ? 'готово' : isGame && gameDay ? `игра ${gameDay}` : `${q.value} из ${q.target}`}
          </span>
        </span>
      </span>
      <span className="bq-reward mono">+{q.reward} XP</span>
      <span className="bq-chev" aria-hidden>
        ›
      </span>
    </button>
  );
}

/** Полёт колоды из ячейки в пилюлю «Колоды N» (WAAPI, дуга, 600 мс). */
function flyDeckToPill(fromEl: HTMLElement, rarity: number) {
  if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches) {
    bumpPill();
    return;
  }
  const pill = document.querySelector('.bp-head-decks');
  if (!pill) return;
  const a = fromEl.getBoundingClientRect();
  const b = pill.getBoundingClientRect();
  const clone = document.createElement('div');
  clone.className = `bp-deck-fly bp-deck-fly--r${rarity}`;
  clone.style.left = `${a.left + a.width / 2 - 11}px`;
  clone.style.top = `${a.top + a.height / 2 - 15}px`;
  document.body.appendChild(clone);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  // Контрольная точка дуги: вверх и в сторону пилюли.
  const midX = dx * 0.45;
  const midY = Math.min(dy * 0.3, 0) - 60;
  const anim = clone.animate(
    [
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      { transform: `translate(${midX}px, ${midY}px) scale(0.7)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.3)`, opacity: 0.9 },
    ],
    { duration: 600, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' },
  );
  anim.onfinish = () => {
    clone.remove();
    bumpPill();
  };
}

function bumpPill() {
  const pill = document.querySelector('.bp-head-decks');
  if (!pill) return;
  pill.classList.remove('bp-pill-bump');
  // reflow, чтобы анимация перезапускалась
  void (pill as HTMLElement).offsetWidth;
  pill.classList.add('bp-pill-bump');
}


function Head({ onBack, decks }: { onBack: () => void; decks?: number }) {
  return (
    <div className="subhead">
      <button className="iconbtn" onClick={onBack} aria-label="Назад">
        <BackIcon />
      </button>
      <div className="title">Straddle Pass</div>
      {decks !== undefined && (
        <Link className="bp-head-decks" to="/pass/decks">
          {/* иконка эпической колоды (макет статус-бара BP Pass) */}
          <svg width="13" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c6b99" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <rect x="6" y="4" width="12" height="16" rx="2.5" fill="rgba(124,107,153,.25)" />
            <path d="M10 4V2.8A1.8 1.8 0 0 1 11.8 1h4.4A1.8 1.8 0 0 1 18 2.8V17" opacity=".5" />
          </svg>
          Колоды<b className="mono">{decks}</b>
        </Link>
      )}
    </div>
  );
}

function TrackCell({
  cell,
  onTap,
  gamesTo,
  justClaimed = false,
}: {
  cell: PassCell | null;
  onTap: (c: PassCell, el?: HTMLElement | null) => void;
  /** «через ~N игр» для закрытых ячеек. */
  gamesTo: number;
  justClaimed?: boolean;
}) {
  if (!cell) return <div className="bp-cell bp-cell--empty" />;
  const name =
    cell.kind === 'deck'
      ? `${RARITY_LABEL[cell.deckRarity ?? 0]} колода`
      : cell.kind === 'voucher'
        ? voucherName(cell.voucherType)
        : cell.meta === 'random_nick_color'
          ? 'Ник «Один цвет»'
          : (cell.item?.name ?? '—');
  const sub =
    cell.state === 'claimed'
      ? 'Получено'
      : cell.state === 'claimable'
        ? 'Забрать'
        : cell.state === 'locked'
          ? RARITY_LABEL[cell.kind === 'deck' ? (cell.deckRarity ?? 0) : (cell.item?.rarity ?? 0)]
          : `через ~${gamesTo} ${gamesTo === 1 ? 'игру' : gamesTo < 5 ? 'игры' : 'игр'}`;
  const rarity = cell.kind === 'deck' ? (cell.deckRarity ?? 0) : (cell.item?.rarity ?? 0);
  return (
    <button
      className={`bp-cell bp-cell--${cell.state}${justClaimed ? ' bp-cell--just' : ''}`}
      style={
        cell.state === 'claimable'
          ? ({
              '--cellc': RARITY_COLOR[rarity],
              '--celltint': rarityTint(rarity, 0.22),
            } as CSSProperties)
          : undefined
      }
      onClick={(e) => onTap(cell, e.currentTarget.querySelector<HTMLElement>('.bp-cell-preview'))}
    >
      <span className="bp-cell-preview">
        <RewardPreview cell={cell} size={50} active={cell.state === 'claimable'} />
        {cell.state === 'claimed' && (
          <span className="bp-cell-check">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4 12.5 9.5 18 20 6.5" />
            </svg>
          </span>
        )}
        {cell.state === 'locked' && (
          <span className="bp-cell-lock">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden>
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </span>
        )}
      </span>
      <span className="bp-cell-main">
        <span className="nm">{name}</span>
        <span
          className={`sub${cell.state === 'claimable' ? ' sub--gold' : cell.state === 'claimed' ? ' sub--ok' : cell.state === 'locked' ? ' rtx' : ''}`}
          style={cell.state === 'locked' ? rarityTextStyle(rarity) : undefined}
        >
          {sub}
        </span>
      </span>
    </button>
  );
}

/** SVG-снежинка (спека: не эмодзи). */
export function SnowIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9" />
    </svg>
  );
}

/** «за игру в пятницу»: винительный падеж + предлог (во вторник). */
const WEEKDAY_ACC = [
  'в воскресенье',
  'в понедельник',
  'во вторник',
  'в среду',
  'в четверг',
  'в пятницу',
  'в субботу',
];

export function voucherName(type: string | null): string {
  switch (type) {
    case 'drink':
      return 'Напиток за 1 ₽';
    case 'entry':
      return 'Бесплатный вход';
    case 'reentry':
      return 'Бесплатный ре-энтри';
    default:
      return 'Ваучер';
  }
}
